/**
 * ALO-70 (Recently Viewed) — tracker + client hydration for snippets/recently-viewed.liquid.
 *
 * Replaces Rebuy widget 267328. Rebuy sent every shopper's browsing history to a
 * third party; this keeps it on the device. Nothing here talks to any origin but
 * ours, and the only requests it makes are GETs for tile markup.
 *
 * THE DATA
 *   key    'aloha:recently-viewed'  — colon-namespaced, matching the theme's
 *                                     best-behaved storage idiom (bb:bonus:shown,
 *                                     assets/bundle-builder.js:521-528). Pointedly
 *                                     NOT 'recentlyViewed': that is the name of the
 *                                     dead cookie in the pre-existing (never
 *                                     rendered, jQuery-based) snippet this replaces,
 *                                     and returning visitors may still carry one in
 *                                     a URL-escaped comma-joined format.
 *   shape  [{ h: <handle>, ts: <epoch ms> }]  — handles and timestamps only. No
 *          customer id, no variant, no page, no referrer, no device signal.
 *   cap    12 entries · expiry 30 days, both enforced on every read AND write, so
 *          a list that has been sitting in storage since before an expiry change
 *          is corrected the next time it is touched rather than at write time only.
 *          These are the only bounds: there is no shopper-facing clear control.
 *
 * This is the theme's first first-party localStorage consumer — every other hit is
 * vendor code (TriplePixel, Rivo) — so the try/catch discipline below is the
 * convention being set, not one being followed. Safari private browsing throws on
 * *access*, not just on write, so reads are guarded too; any throw degrades to
 * "no history", which leaves the rail hidden and is the intended behaviour when
 * storage is unavailable.
 *
 * No consent gate, deliberately. See the snippet header.
 */
(function () {
  'use strict';

  var KEY = 'aloha:recently-viewed';
  var MAX_ENTRIES = 12;
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var SECTION_ID = 'recently-viewed-tile';

  /* ---------------------------------------------------------------- storage */

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      var cutoff = Date.now() - MAX_AGE_MS;
      return list
        .filter(function (e) { return e && typeof e.h === 'string' && typeof e.ts === 'number' && e.ts > cutoff; })
        .slice(0, MAX_ENTRIES);
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_ENTRIES)));
    } catch (e) {
      /* storage full, disabled, or private mode — history is a nicety, drop it */
    }
  }

  /** Record a view: newest first, one entry per handle. */
  function record(handle) {
    if (!handle) return;
    var list = read().filter(function (e) { return e.h !== handle; });
    list.unshift({ h: handle, ts: Date.now() });
    write(list);
  }

  /** Drop a handle that no longer resolves to a sellable product. */
  function forget(handle) {
    write(read().filter(function (e) { return e.h !== handle; }));
  }

  /* ------------------------------------------------------------------ fetch */

  /**
   * Section Rendering API URL for one tile. root is routes.root_url, so the
   * request stays inside the viewer's market ('/' for US, '/en-ca' for Canada)
   * and prices come back in that market's currency.
   */
  function tileUrl(root, handle) {
    var base = root === '/' ? '' : root.replace(/\/$/, '');
    return base + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
  }

  /**
   * Resolves to a tile element, or null.
   *
   * Shopify answers an unknown handle with 200 and a nil product, NOT 404 — so the
   * presence of a tile in the response is the only reliable signal. A null result
   * therefore means "deleted, unpublished, or not sellable in this market", and the
   * caller prunes the handle.
   */
  function fetchTile(root, handle) {
    return fetch(tileUrl(root, handle), { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-rv-tile]');
      })
      .catch(function () { return null; });
  }

  /* -------------------------------------------------------------- scheduling */

  /**
   * Runs fn once the page is done with the important work.
   *
   * Uses window.alohaIdle (snippets/header-embeds.liquid:21-24, rendered from
   * layout/theme.liquid:319) — the theme's own idle hook, which waits for `load`
   * plus 1500ms and is what Hotjar, Amazon, Bing and Quantcast are deferred with
   * in snippets/footer-helpers.liquid:55. Using it puts this rail in the same queue
   * as everything else that is deliberately not urgent.
   *
   * An earlier pass gated hydration on an IntersectionObserver over the rail, on the
   * reasoning that the rail sits at the foot of a ~6000px page and most shoppers
   * never reach it, so the fetches may as well never happen. That was the wrong
   * trade. IntersectionObserver does not fire while a tab is hidden, which is a
   * routine state — a PDP opened in a background tab from a collection grid — and it
   * made the feature's entire behaviour depend on geometry and visibility that
   * cannot be reasoned about from the markup. The cost of dropping it is up to five
   * ~4KB same-origin GETs after load, each cacheable per product and market; the
   * benefit is that the rail is populated before it is scrolled to, so it never
   * pops in, and that the trigger is deterministic enough to actually test.
   */
  function idle(fn) {
    if (typeof window.alohaIdle === 'function') { window.alohaIdle(fn); return; }
    var run = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 3000 });
      else setTimeout(fn, 1500);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  /* ------------------------------------------------------------------- rail */

  function RecentlyViewed(root) {
    this.el = root;
    this.track = root.querySelector('[data-rv-track]');
    this.root = root.dataset.rvRoot || '/';
    this.limit = parseInt(root.dataset.rvLimit, 10) || 5;
    this.min = parseInt(root.dataset.rvMin, 10) || 2;
    this.showAtc = root.dataset.rvAtc === '1';
    this.current = root.dataset.rvCurrent || '';
  }

  RecentlyViewed.prototype.init = function () {
    // The current product is recorded before anything else, so a first-ever PDP
    // visit still counts even though its own rail will not render.
    if (this.current) record(this.current);

    var self = this;
    var handles = read()
      .map(function (e) { return e.h; })
      .filter(function (h) { return h !== self.current; })
      .slice(0, this.limit);

    if (handles.length < this.min) return;

    this.bindActions();
    idle(function () { self.hydrate(handles); });
  };

  RecentlyViewed.prototype.hydrate = function (handles) {
    var self = this;

    // Requested in parallel but appended in the array's order, so the rail reads
    // most-recent-first regardless of which response lands first.
    Promise.all(handles.map(function (h) { return fetchTile(self.root, h); }))
      .then(function (tiles) {
        var shown = 0;

        tiles.forEach(function (tile, i) {
          if (!tile) { forget(handles[i]); return; }
          // Parsed in a detached document, so adopt it explicitly rather than
          // relying on appendChild's implicit adoption.
          var node = document.importNode(tile, true);
          if (!self.showAtc) {
            var add = node.querySelector('[data-rv-add]');
            if (add) add.remove();
          }
          self.track.appendChild(node);
          shown++;
        });

        // Re-checked after fetching, not just before: handles can resolve to
        // nothing (deleted product, or one this market does not sell), and a rail
        // of one tile is not a rail.
        if (shown < self.min) {
          self.track.innerHTML = '';
          return;
        }

        // Reveals the rail. Not el.hidden — see the CSS note in
        // snippets/recently-viewed.liquid: the section has to keep a box for the
        // observer above to fire at all.
        self.el.dataset.rvReady = '1';
      });
  };

  RecentlyViewed.prototype.bindActions = function () {
    var self = this;

    // Delegated, because the tiles do not exist yet when this runs.
    //
    // AlohaCart owns adding to cart. It binds form[action$="/cart/add"] exactly
    // once at DOMContentLoaded with no delegation and no MutationObserver
    // (assets/aloha-cart.js:182-184), so a form injected later would escape the
    // intercept and do a native page-reload POST — which is why the tile ships a
    // button instead. addToCart() adds and refreshes but does not open the drawer
    // (:1325), so the drawer is asked to open through the documented 'cart:open'
    // event it already listens for (:158). One add, one handler.
    this.track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-rv-add]');
      if (!btn) return;
      e.preventDefault();

      var variantId = btn.dataset.variantId;
      var cart = window.AlohaCart;
      if (!variantId || !cart || typeof cart.addToCart !== 'function') return;

      btn.disabled = true;
      Promise.resolve(cart.addToCart(variantId, 1))
        .then(function () { document.dispatchEvent(new CustomEvent('cart:open')); })
        .finally(function () { btn.disabled = false; });
    });
  };

  /* ------------------------------------------------------------------- boot */

  function boot() {
    var el = document.getElementById('aloha-rv');
    if (!el || el.dataset.rvInit) return;
    el.dataset.rvInit = '1';
    new RecentlyViewed(el).init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
